import {
  countActedAlarms,
  countedClassifications,
  countedOrigins,
} from './attentionMapAggregator';

/**
 * A Barra de Controles oferece exatamente o que a tela conta — nem mais, nem
 * menos. São as mesmas listas de propósito: um controle que oferecesse um valor
 * não contado devolveria tela vazia sem explicação, e um valor contado sem
 * controle seria informação inalcançável.
 *
 * Observação e Rede Digital ficam de fora dos dois.
 */
export const filterableClassifications = countedClassifications;
export const filterableOrigins = countedOrigins;

function normalize(value) {
  return String(value || '')
    .toLowerCase()
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '');
}

function isValidValue(value, allowed) {
  return allowed.indexOf(value) >= 0;
}

/**
 * Mantém só os valores que a tela conhece, para um link salvo com lixo na query
 * não virar uma tela vazia sem explicação.
 */
export function normalizeValues(values, allowed) {
  const list = Array.isArray(values) ? values : String(values || '').split(',');

  return list
    .map((value) => String(value || '').trim())
    .filter((value) => isValidValue(value, allowed));
}

/**
 * O Ativo casa com o recorte quando tem **um Alarme Atuado que é as duas coisas
 * ao mesmo tempo**: da Origem pedida e da Classificação pedida.
 *
 * Cruzar os dois eixos, em vez de testá-los em separado, é o que faz
 * *Crítico + Autodiagnóstico* significar um autodiagnóstico crítico, e não um
 * crítico qualquer convivendo com um autodiagnóstico qualquer.
 *
 * Antes isto olhava só o alarme principal, e por isso mentia: um Ativo com um
 * Crítico e um Ordinário sumia ao filtrar por Ordinário, porque o principal era o
 * Crítico. Contar resolve — o Ordinário está lá, e agora é visto.
 *
 * Ativo sem Alarme Atuado não casa com recorte de alarme nenhum, e some quando
 * qualquer um dos dois eixos está ligado.
 */
function matchesAlarms(asset, classifications, origins) {
  if (!classifications.length && !origins.length) {
    return true;
  }

  return countActedAlarms(asset.actedAlarms, origins, classifications) > 0;
}

/**
 * A busca varre o que está escrito na tela: ativo, instalação, empresa e regional.
 *
 * Nome de alarme saiu junto com o alarme principal — o marcador e o popup deixaram
 * de exibi-lo, e busca que acha o que não está escrito devolve resultado que o
 * usuário não consegue explicar. Quem procura por nome de alarme tem a lista ao
 * lado.
 */
function matchesQuery(asset, query) {
  if (!query) {
    return true;
  }

  return [
    asset.name,
    asset.installation,
    asset.company,
    asset.regional,
  ].some((field) => normalize(field).indexOf(query) >= 0);
}

/**
 * Filtro do Mapa: o que não casa **some** do mapa e da lista, ao contrário do
 * Painel de Status de Comunicação, que esmaece. Os Agrupamentos do Mapa contam
 * marcadores, e um número que misturasse realçado com apagado mentiria.
 *
 * @param {Object[]} assets Ativos do escopo.
 * @param {Object} criteria `{ classifications, origins, query }`.
 * @returns {Object[]} Os Ativos que ficam visíveis.
 */
export default function applyAttentionMapFilter(assets, criteria) {
  const {
    classifications = [],
    origins = [],
    query = '',
  } = criteria || {};

  const normalizedQuery = normalize(query).trim();

  return (assets || []).filter(
    (asset) => matchesAlarms(asset, classifications, origins)
      && matchesQuery(asset, normalizedQuery),
  );
}

/** Diz se algum recorte está ativo, para a tela anunciar que mostra um recorte. */
export function isFilterActive(criteria) {
  const {
    classifications = [],
    origins = [],
    query = '',
  } = criteria || {};

  return !!(
    classifications.length
    || origins.length
    || String(query).trim()
  );
}
